"use client";
import type { ChangeEvent, SelectHTMLAttributes } from "react";
import React, { useEffect, useRef, useState } from "react";
import type { Nil } from "cy-util";
import type { VariantProps } from "class-variance-authority";
import { cva, cx } from "class-variance-authority";
import type { FieldState } from "../models";
import { useFormGroup } from "../FormGroup";
import { useComposedRefs, useRouteState } from "../../../hooks";
import type { FormControlVariantsProps } from "../classes";
import { formControlVariants } from "../classes";
import { LoadingDots } from "../../Loaders";

export interface SelectItem extends Record<string, any> {
  id: string;
  name: string;
  disabled?: boolean;
}

export interface UseSelectOptions {
  useRouteState?: boolean;
  name?: string;
}

export function useSelect(
  options: SelectItem[] | Nil,
  selectOptions: UseSelectOptions = {},
) {
  const key = selectOptions.name || "select";
  const routeState = useRouteState();
  const state = useState<string | undefined>();
  const route = routeState.useState(key);

  const [selectedId, setSelectedId] = selectOptions.useRouteState
    ? route
    : state;

  function onChange(evt: any) {
    setSelectedId(evt.target.value);
  }

  useEffect(() => {
    if (
      !selectedId &&
      options &&
      options.length > 0 &&
      !options.find((x) => x.id === "")
    ) {
      setSelectedId(options[0].id);
    }
  }, [options, selectedId, setSelectedId]);

  const selectedItem = options?.find((x) => x.id === selectedId);
  return {
    setSelectedId,
    selectedItem,
    selectedId,
    selectProps: {
      options,
      value: selectedId || (options || [])[0]?.id,
      onChange,
    },
  };
}

const selectStyles = cva(["block w-full pl-3 pr-10 py-0"], {
  variants: {
    vEmpty: {
      true: "text-gray-400",
      false: "",
    },
  },
});

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    VariantProps<typeof selectStyles>,
    FormControlVariantsProps {
  className?: string;
  options: SelectItem[] | Nil;
  placeholder?: string;
  id?: string;
  addBlankOption?: boolean;
  displayValue?: (obj: any) => string;
  fieldState?: FieldState;
  loading?: boolean;
  "data-testid"?: string;
}

/**
 *
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      className,
      vSize,
      id,
      placeholder,
      options,
      addBlankOption,
      name,
      displayValue,
      fieldState,
      disabled,
      value,
      onChange,
      loading,
      "data-testid": dataTestId,
      ...rest
    },
    forwardedRef,
  ) {
    const formGroup = useFormGroup();
    fieldState = fieldState || formGroup?.controller?.fieldState;
    const error = fieldState?.error;
    const controllerValue = formGroup?.value;
    const selectValue = controllerValue !== undefined ? controllerValue : value;
    displayValue = displayValue || ((obj: any) => obj.name);
    const selectRef = useRef<HTMLSelectElement>(null);

    const ref = useComposedRefs(forwardedRef, selectRef);
    options = options || [];

    const _onChange = (e: ChangeEvent<HTMLSelectElement>) => {
      onChange && onChange(e);
    };

    const valueProp = value != null ? { value } : undefined;

    // Auto-generate data-testid from name if not provided
    const generateTestId = () => {
      if (dataTestId) return dataTestId;
      if (name) return `select-${name}`;
      if (formGroup?.id) return `select-${formGroup.id}`;
      return "select";
    };

    const testId = generateTestId();

    return (
      <div className="relative flex items-center">
        <select
          className={cx(
            selectStyles({
              className,
              vEmpty: !selectValue,
            }),
            formControlVariants({ vSize, vColor: error ? "error" : undefined }),
          )}
          id={id}
          onChange={_onChange}
          ref={ref}
          data-testid={testId}
          {...valueProp}
          disabled={disabled}
          name={name}
          {...rest}
          value={selectValue}
        >
          {addBlankOption ? (
            <option
              className=""
              value=""
              data-testid="select-option-placeholder"
            >
              {placeholder}
            </option>
          ) : null}
          {options.map((x) => (
            <option
              className="p-5 text-black"
              disabled={!!x.disabled}
              key={x.id}
              value={x.id}
              data-testid={`select-option-${x.id}`}
            >
              {displayValue?.(x)}
            </option>
          ))}
        </select>
        {loading ? (
          <LoadingDots className="absolute right-10" height={10} />
        ) : null}
      </div>
    );
  },
);
